import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.function.name.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { Radio as Radio$1 } from '@mui/joy';
import classNames from 'classnames';
import { getThemeClassNames } from '@cognite/cogs-utils';
import { Flex } from '../flex/flex.js';
import 'core-js/modules/es.object.assign.js';
import 'tslib';
import { Skeleton } from '../skeleton/skeleton.js';

/**
 * Radio
 * =============================================================================
 * Radio buttons allow users to select one option from a set.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-button--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Radio name="radio" checked label="Your label" />
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param checked         - Check or uncheck the checkbox.
 * @param color           - The color of the checkbox. Don't over-use this!
 * @param disabled        - Disable the checkbox.
 * @param label           - Show a label besides the Radio.
 * @param name            - Native `name` HTML attribute.
 * @param onChange        - The function fired when the checkbox is changed.
 * @param radioRef        - Ref the native HTML radio component.
 * @param readOnly        - If true, the user can't change the checkbox.
 * @param skeleton        - The skeleton version of the radio button.
 * @param style           - Overwrite and extend the default styling with custom styles.
 * @param value           - Native `value` HTML attribute.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-button--base)
 */
var Radio = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var checked = _ref.checked,
    className = _ref.className,
    disabled = _ref.disabled,
    id = _ref.id,
    name = _ref.name,
    inverted = _ref.inverted,
    _onChange = _ref.onChange,
    readOnly = _ref.readOnly,
    style = _ref.style,
    value = _ref.value,
    label = _ref.label,
    _ref$skeleton = _ref.skeleton,
    skeleton = _ref$skeleton === undefined ? false : _ref$skeleton,
    themeProps = _ref.themeProps;
  var extendedId = id || "" + (name || '') + value;
  return skeleton ? jsxs(Flex, {
    className: classNames.apply(undefined, getThemeClassNames(inverted, themeProps)),
    direction: "row",
    gap: 10,
    children: [jsx(Skeleton.Circle, {
      diameter: "16px"
    }), label && jsx(Skeleton.Rectangle, {
      width: "100px",
      height: "16px"
    })]
  }) : jsx(Radio$1, {
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-radio'].concat(getThemeClassNames(inverted, themeProps), [disabled && 'cogs-radio--disabled', className])),
    ref: ref,
    id: extendedId,
    name: name,
    style: style,
    value: value,
    onChange: function onChange(event) {
      return _onChange && _onChange(event, value);
    },
    disabled: disabled,
    checked: checked,
    readOnly: readOnly,
    label: label,
    slotProps: {
      radio: {
        className: 'cogs-radio__radio'
      },
      input: {
        className: 'cogs-radio__input',
        role: 'radio'
      },
      label: {
        className: 'cogs-radio__label'
      },
      action: {
        className: 'cogs-radio__action'
      }
    }
  });
});

export { Radio };
